import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { MotifInput } from "@ey-xd/motif-react";
import { Controller } from "react-hook-form";
import {
  formatAmountUnitDecimalPlace,
  formatAmountWithCommas,
} from "@/utility/common";
import { GetCommonDeleteImgButton } from "@/components/Images/CommonImages";

const useDefaultUnitDetails = () => {
  const { defaultUnit } = useSelector((state: any) => state.case);
  const decimalPlace = defaultUnit?.decimalPlace;
  const defaultUnitName = defaultUnit?.name;
  return { decimalPlace, defaultUnitName };
};

const CustomHeader = () => {
  const { defaultUnitName } = useDefaultUnitDetails();
  const unitName = defaultUnitName;
  return `(${unitName})`;
};
const amountCellRenderer = (field: string) => (params: any) => {
  if (params.node.rowPinned) {
    return "";
  }
  const { decimalPlace, defaultUnitName } = useDefaultUnitDetails();
  const amount = params.data[field];
  const formattedDefaultAmount = formatAmountUnitDecimalPlace(
    amount,
    decimalPlace
  );
  return formattedDefaultAmount !== "-"
    ? `${formattedDefaultAmount} ${defaultUnitName}`
    : formattedDefaultAmount;
};

const accountUnitAmountRenderer =
  (field: string, unitDecimalPlace: any, accountUnit: any) => (params: any) => {
    if (params.node.rowPinned) {
      return "";
    }
    const amount = params.data[field];
    let formattedAmount = amount;
    if (unitDecimalPlace !== null) {
      formattedAmount = formatAmountWithCommas(amount, unitDecimalPlace);
    }
    if (accountUnit) {
      formattedAmount += ` ${accountUnit}`;
    }
    return formattedAmount;
  };

export const useColumnManualDefs = (
  control: any,
  paymentOption: string,
  handleDelete: (id: string) => void,
  handleValueChange: (rowIndex: number, value: string) => void,
  unitDecimalPlace: any,
  accountUnit: any
) => {
  const { t } = useTranslation();
  //const decimalLimit = unitDecimalPlace ? unitDecimalPlace : 8;

  return [
    {
      field: "claimItemReference",
      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.claimItemId"
      ),
      flex: 1,
      minWidth: 100,

      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
      },
      autoHeight: true,
    },
    {
      field: "category",
      minWidth: 100,

      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.category"
      ),
      flex: 1,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
      },
      autoHeight: true,
    },
    {
      field: "currentAmount",
      minWidth: 180,

      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.currentAmount",
        {
          unitName: CustomHeader(),
        }
      ),
      flex: 1,
      cellRenderer: amountCellRenderer("currentAmount"),
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
      },
      autoHeight: true,
      headerHeight: 130,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
    },
    {
      field: "resolvedAmount",

      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.resolvedAmount",
        {
          unitName: CustomHeader(),
        }
      ),
      flex: 1,
      minWidth: 180,
      cellRenderer: amountCellRenderer("resolvedAmount"),
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
      },
      autoHeight: true,
      headerHeight: 130,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
    },
    {
      field: "entitlementAmount",
      minWidth: 180,

      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.entitlement"
      ),
      flex: 1,
      autoHeight: true,
      headerHeight: 130,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
      cellRenderer: accountUnitAmountRenderer(
        "entitlementAmount",
        unitDecimalPlace,
        accountUnit
      ),
    },
    {
      field: "priorSettledAmount",
      minWidth: 180,

      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.priorSettled"
      ),
      flex: 1,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
      cellRenderer: accountUnitAmountRenderer(
        "priorSettledAmount",
        unitDecimalPlace,
        accountUnit
      ),
    },
    {
      field: "settlementAmount",
      minWidth: 180,

      headerName: t(
        "pages.paymentStatus.paymentSetup.claimItems.tableColumnsDetails.settlement"
      ),
      width: 200,
      cellRenderer: (params: any) => {
        if (params.node.rowPinned) {
          return params.value;
        }
        if (paymentOption === "1") {
          return (
            <Controller
              name={`generatePaymentItems[${params.rowIndex}].settlementAmount`}
              control={control}
              defaultValue={params.data.settlementAmount}
              render={({ field }) => {
                return (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <MotifInput
                      {...field}
                      //@ts-ignore
                      className="ag-header-cell-comp-wrapper"
                      value={field.value}
                      pattern={`^-?\\d*\\.?\\d{0,${
                        unitDecimalPlace || unitDecimalPlace === 0
                          ? unitDecimalPlace
                          : 8
                      }}$`}
                      onChange={(e: any) => {
                        const value = e.target.value;
                        const regex = new RegExp(
                          `^-?\\d*\\.?\\d{0,${
                            unitDecimalPlace || unitDecimalPlace === 0
                              ? unitDecimalPlace
                              : 8
                          }}$`
                        );
                        if (regex.test(value)) {
                          field.onChange(value);
                        } else {
                          e.preventDefault();
                        }
                      }}
                      onBlur={(e: any) => {
                        let value = e.target.value;
                        //const amountWithDecimalPlace = parseFloat(value).toFixed(decimalLimit);
                        //value = parseFloat(amountWithDecimalPlace);
                        handleValueChange(params.rowIndex, value);
                      }}
                      onMouseLeave={(e: any) => {
                        e.target.blur();
                      }}
                      hideClearButton={true}
                    />
                    <span style={{ marginLeft: "8px" }}>{accountUnit}</span>
                  </div>
                );
              }}
            />
          );
        } else {
          return accountUnitAmountRenderer(
            "settlementAmount",
            unitDecimalPlace,
            accountUnit
          )(params);
        }
      },
      autoHeight: true,
      headerHeight: 150,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
    },
    {
      field: "action",
      headerName: "",
      flex: 1,
      minWidth: 60,

      sortable: false,
      cellRenderer: (params: any) => {
        if (params.node.rowPinned) {
          return null;
        }
        return (
          <>
            {/* <div className="motif-row">
					 	<div className="motif-col-lg-6"> */}
            <GetCommonDeleteImgButton
              onClick={() => {
                handleDelete(params.data.claimItemId);
              }}
            ></GetCommonDeleteImgButton>
            {/* <MotifButton
								size="small"
								variant={DELETE_IMG_VARIANT}
								type="button"
								onClick={() => {
									handleDelete(params.data.claimItemId);
								}}
							>
								{getCommonDeleteImg()}
							</MotifButton> */}
            {/* </div>
					 </div> */}
          </>
        );
      },
    },
  ];
};
